/** @jsxImportSource @emotion/react */

import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { Button } from '@mui/material'
import { FC, useRef } from 'react'
import { findAllVideoCategory_findAllVideoCategory_items as VideoCategory } from '__generatedTypes__/findAllVideoCategory'
import { RouteValue, cmsRoutes, useLink } from 'routes/cmsRoutes'
import { Popper } from 'components/Popper'
import { ReactComponent as QuizIcon } from 'assets/icons/quiz.svg'
import { ReactComponent as CheckIcon } from 'assets/icons/check-grey.svg'
import { ReactComponent as GridRowsIcon } from 'assets/icons/grid-rows.svg'
import { ReactComponent as GridPlusIcon } from 'assets/icons/grid-plus.svg'

import * as comparisonTableClasses from 'pagesNew/UpgradePage/ComparisonTable/styles'
import * as classes from './styles'

export const VIDEO_TYPES = {
  VIDEO: 'video',
  RESOURCE: 'resource',
  VIDEO_QUIZ: 'video-quiz',
  CHAPTER_COMPLETION: 'chapter-completion',
} as const

export type VideoTypeKey = keyof typeof VIDEO_TYPES
export type VideoType = (typeof VIDEO_TYPES)[VideoTypeKey]

type Props = {
  category?: VideoCategory
  hiddenTypes?: VideoType[]
}

export const getRouteByVideoType = (videoType: VideoType, isEdit?: boolean): RouteValue => {
  if (videoType === VIDEO_TYPES.RESOURCE) {
    return cmsRoutes[isEdit ? 'VIDEOS_RESOURCE_EDIT' : 'VIDEOS_RESOURCE']
  }

  if (videoType === VIDEO_TYPES.VIDEO) {
    return cmsRoutes[isEdit ? 'VIDEOS_VIDEO_EDIT' : 'VIDEOS_VIDEO']
  }

  if (videoType === VIDEO_TYPES.VIDEO_QUIZ) {
    return cmsRoutes[isEdit ? 'VIDEOS_QUIZ_EDIT' : 'VIDEOS_QUIZ']
  }

  if (videoType === VIDEO_TYPES.CHAPTER_COMPLETION) {
    return cmsRoutes.VIDEOS_CHAPTER_COMPLETION_EDIT
  }

  return '' as RouteValue
}

const VIDEO_TYPE_OPTIONS = [
  {
    type: VIDEO_TYPES.RESOURCE,
    label: 'Resource',
    icon: GridPlusIcon,
  },
  {
    type: VIDEO_TYPES.VIDEO,
    label: 'Video',
    icon: GridRowsIcon,
  },
  {
    type: VIDEO_TYPES.VIDEO_QUIZ,
    label: 'Quiz',
    icon: QuizIcon,
  },
  {
    type: VIDEO_TYPES.CHAPTER_COMPLETION,
    label: 'Chapter Completion',
    icon: CheckIcon,
  },
] as const

export const AddContentButton: FC<Props> = ({ category, hiddenTypes = [] }) => {
  const button = useRef<HTMLButtonElement>(null)
  const { navigate } = useLink()

  const handleOptionClick = (option: VideoType) => () => {
    if (!category) {
      return
    }

    const categoryParam = { videoCategoryId: category.id }

    navigate(getRouteByVideoType(option), categoryParam)
  }

  const renderPopperContent = () => (
    <ul css={comparisonTableClasses.popperList} style={{ width: button.current ? button.current.clientWidth : 'auto' }}>
      {VIDEO_TYPE_OPTIONS.filter(({ type }) => {
        // Check if type is available
        if (hiddenTypes.includes(type)) {
          return false
        }

        // Special case: hide chapter completion if already exists
        if (type === VIDEO_TYPES.CHAPTER_COMPLETION && category?.chapterCompletionTitle) {
          return false
        }

        return true
      }).map(({ type, label, icon: Icon }) => (
        <li key={type}>
          <Button css={comparisonTableClasses.popperListButton} variant="outlined" onClick={handleOptionClick(type)}>
            <Icon />
            {label}
          </Button>
        </li>
      ))}
    </ul>
  )

  return (
    <Popper trigger="onClick" css={classes.popper} getContent={renderPopperContent}>
      <Button color="blue" size="large" variant="outlined" css={classes.addVideoButton} ref={button}>
        <AddRoundedIcon />
        Add Content
      </Button>
    </Popper>
  )
}
